import { useState, useMemo } from "react"
import { Search, UserPlus, X, User, ChevronDown, ChevronUp, Mail } from "lucide-react"
import { Input } from "@/clientWeb/components/ui/input"
import { Button } from "@/clientWeb/components/ui/button"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import { Badge } from "@/clientWeb/components/ui/badge"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

// Dummy contacts data
const DUMMY_CONTACTS = [
  { id: "1", email: "akshay.g@instavc.com", name: "Akshay G" },
  { id: "2", email: "akshay23@yopmail.com", name: "Akshay 23" },
  { id: "3", email: "akshayinst@yopmail.com", name: "Akshay Inst" },
  { id: "4", email: "ramakrishna@instavc.com", name: "Ramakrishna" },
  { id: "5", email: "syed@instavc.com", name: "Syed Yusuf" },
  { id: "6", email: "mike@instavc.com", name: "Mike Chen" },
  { id: "7", email: "sarah@instavc.com", name: "Sarah Johnson" },
  { id: "8", email: "david@instavc.com", name: "David Lee" },
]

interface ContactsDropdownProps {
  selectedContacts: string[]
  onContactsChange: (contacts: string[]) => void
}

export default function ContactsDropdown({ selectedContacts, onContactsChange }: ContactsDropdownProps) {
  const t = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [tempSelected, setTempSelected] = useState<string[]>(selectedContacts)

  const filteredContacts = useMemo(() => {
    if (!searchQuery) return DUMMY_CONTACTS
    const q = searchQuery.toLowerCase()
    return DUMMY_CONTACTS.filter(c => c.email.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))
  }, [searchQuery])

  const handleToggle = (id: string) =>
    setTempSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const handleInsert = () => { onContactsChange(tempSelected); setExpanded(false); setSearchQuery("") }
  const handleCancel = () => { setTempSelected(selectedContacts); setSearchQuery(""); setExpanded(false) }
  const handleRemoveChip = (id: string) => {
    const n = selectedContacts.filter(x => x !== id)
    onContactsChange(n)
    setTempSelected(n)
  }
  const getContact = (id: string) => DUMMY_CONTACTS.find(c => c.id === id)

  return (
    <div className="space-y-3">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => { setExpanded(v => !v); setTempSelected(selectedContacts) }}
        className="w-full flex items-center justify-between h-11 px-4 border border-border rounded-lg bg-background hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-primary" />
          <span className="text-sm text-foreground font-medium">
            {selectedContacts.length > 0
              ? `${selectedContacts.length} ${t("common.participants").toLowerCase()} ${t("common.added").toLowerCase()}`
              : t("meetings.addParticipants")}
          </span>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
          : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {/* Inline expanded panel */}
      {expanded && (
        <div className="border border-border rounded-xl overflow-hidden bg-card shadow-sm">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/10">
            <span className="text-sm font-semibold text-foreground">{t("presence.contacts")}</span>
            <span className="text-xs text-muted-foreground">{tempSelected.length} {t("common.selected").toLowerCase()}</span>
          </div>
          <div className="p-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("presence.searchContacts")}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <div className="space-y-0.5 max-h-52 overflow-y-auto -mx-1 px-1">
              {filteredContacts.map(contact => {
                const selected = tempSelected.includes(contact.id)
                return (
                  <div
                    key={contact.id}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                      selected ? "bg-primary/10" : "hover:bg-muted/40"
                    }`}
                    onClick={() => handleToggle(contact.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground leading-tight">{contact.name}</p>
                        <p className="text-xs text-muted-foreground">{contact.email}</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => handleToggle(contact.id)}
                      className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                )
              })}
              {filteredContacts.length === 0 && (
                <div className="text-center py-6 text-sm text-muted-foreground">{t("presence.noContacts")}</div>
              )}
            </div>
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button type="button" variant="outline" onClick={handleCancel} className="flex-1 h-9 text-sm">{t("common.cancel")}</Button>
              <Button type="button" onClick={handleInsert} disabled={tempSelected.length === 0} className="flex-1 h-9 text-sm">
                {t("common.insert")} ({tempSelected.length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Selected chips (shown when collapsed) */}
      {selectedContacts.length > 0 && !expanded && (
        <div className="flex flex-wrap gap-2">
          {selectedContacts.map(id => {
            const contact = getContact(id)
            if (!contact) return null
            return (
              <Badge key={id} variant="secondary" className="px-2.5 py-1.5 bg-primary/10 border-primary/20 text-primary dark:border-primary/30 flex items-center gap-1.5 text-xs">
                <Mail className="w-3 h-3" />
                <span className="font-medium">{contact.email}</span>
                <button onClick={() => handleRemoveChip(id)} className="hover:text-red-500 transition-colors ml-0.5">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )
          })}
        </div>
      )}
    </div>
  )
}
